/**
 * TIMELINE HELPERS
 *
 * Utility functions for grouping and organizing activity events by time periods.
 * Provides timeline grouping logic for the activity feed (Today, Yesterday, This Week, etc.)
 *
 * @module timelineHelpers
 */

import type { ActivityEvent } from '@/shared/types/activityEvent';
import { Timestamp } from 'firebase/firestore';

/**
 * Time group categories for organizing events
 */
export type TimeGroup = 
  | 'today' 
  | 'yesterday' 
  | 'this_week' 
  | 'last_week' 
  | 'this_month' 
  | 'last_month' 
  | 'earlier';

/**
 * Represents a group of events within a specific time period
 */
export interface TimeGrouping {
  /** The time period category */
  group: TimeGroup;
  
  /** Display label for the group (e.g., "Today · 8 updates") */
  label: string;
  
  /** Start date of the time period */
  startDate: Date;
  
  /** End date of the time period */
  endDate: Date;
  
  /** Number of events in this group */
  eventCount: number;
  
  /** Array of events or event groups in this group, sorted newest first */
  events: ActivityEvent[];
}

/**
 * Get the start of today (midnight)
 */
function getStartOfToday(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

/**
 * Get the start of yesterday (midnight)
 */
function getStartOfYesterday(): Date {
  const date = getStartOfToday();
  date.setDate(date.getDate() - 1);
  return date;
}

/**
 * Get the start of this week (Monday at midnight)
 * Week starts on Monday (weekStartsOn: 1)
 */
function getStartOfThisWeek(): Date {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = day === 0 ? -6 : 1 - day; // Calculate days to Monday
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/**
 * Get the start of last week (Monday at midnight)
 */
function getStartOfLastWeek(): Date {
  const thisWeek = getStartOfThisWeek();
  const lastWeek = new Date(thisWeek);
  lastWeek.setDate(thisWeek.getDate() - 7);
  return lastWeek;
}

/**
 * Get the start of this month (first day at midnight)
 */
function getStartOfThisMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Get the start of last month (first day at midnight)
 */
function getStartOfLastMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
}

/**
 * Get the start date for a time group
 */
function getGroupStartDate(group: TimeGroup): Date {
  switch (group) {
    case 'today':
      return getStartOfToday();
    case 'yesterday':
      return getStartOfYesterday();
    case 'this_week':
      return getStartOfThisWeek();
    case 'last_week':
      return getStartOfLastWeek();
    case 'this_month':
      return getStartOfThisMonth();
    case 'last_month':
      return getStartOfLastMonth();
    case 'earlier':
      return new Date(0); // Beginning of time
  }
}

/**
 * Get the end date for a time group
 */
function getGroupEndDate(group: TimeGroup): Date {
  switch (group) {
    case 'today':
      return new Date(); // Now
    case 'yesterday':
      return getStartOfToday();
    case 'this_week':
      return new Date(); // Now
    case 'last_week':
      return getStartOfThisWeek();
    case 'this_month':
      return new Date(); // Now
    case 'last_month':
      return getStartOfThisMonth();
    case 'earlier':
      return getStartOfLastMonth();
  }
}

/**
 * Get display label for a time group
 * Format: "Today · 8 updates", "Yesterday · 12 updates", etc.
 */
function getGroupLabel(group: TimeGroup, count: number): string {
  const groupNames: Record<TimeGroup, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This Week',
    last_week: 'Last Week',
    this_month: 'This Month',
    last_month: 'Last Month',
    earlier: 'Earlier',
  };
  
  const updateText = count === 1 ? 'update' : 'updates';
  return `${groupNames[group]} · ${count} ${updateText}`;
}

/**
 * Get timestamp from an event or event group
 */
function getTimestamp(item: ActivityEvent): Timestamp {
  return item.timestamp;
}

/**
 * Determine which time group an event belongs to
 */
function determineTimeGroup(eventDate: Date): TimeGroup {
  const startOfToday = getStartOfToday();
  const startOfYesterday = getStartOfYesterday();
  const startOfThisWeek = getStartOfThisWeek();
  const startOfLastWeek = getStartOfLastWeek();
  const startOfThisMonth = getStartOfThisMonth();
  const startOfLastMonth = getStartOfLastMonth();

  // Check in order: today → yesterday → this_week → last_week → this_month → last_month → earlier
  if (eventDate >= startOfToday) {
    return 'today';
  } else if (eventDate >= startOfYesterday) {
    return 'yesterday';
  } else if (eventDate >= startOfThisWeek) {
    return 'this_week';
  } else if (eventDate >= startOfLastWeek) {
    return 'last_week';
  } else if (eventDate >= startOfThisMonth) {
    return 'this_month';
  } else if (eventDate >= startOfLastMonth) {
    return 'last_month';
  } else {
    return 'earlier';
  }
}

/**
 * Group activity events by time periods
 *
 * Groups events into time periods (Today, Yesterday, This Week, etc.),
 * sorts events within each group by timestamp (newest first),
 * and filters out empty groups.
 *
 * NOTE: This function is computationally expensive for large datasets.
 * Consider using the memoized version (memoizedGroupEventsByTime) in React
 * components with useMemo to avoid unnecessary recalculations.
 *
 * @param events - Array of activity events to group
 * @returns Array of time groupings with events
 *
 * @example
 * ```tsx
 * const groupedEvents = groupEventsByTime(events);
 * groupedEvents.forEach(group => {
 *   console.log(group.label); // "Today · 8 updates"
 *   group.events.forEach(event => {
 *     // Display event
 *   });
 * });
 * ```
 */
function groupEventsByTime(events: (ActivityEvent)[]): TimeGrouping[] {
  // Initialize groups
  const groups: Record<TimeGroup, (ActivityEvent)[]> = {
    today: [],
    yesterday: [],
    this_week: [],
    last_week: [],
    this_month: [],
    last_month: [],
    earlier: [],
  };

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort((a, b) => {
    return getTimestamp(b).toDate().getTime() - getTimestamp(a).toDate().getTime();
  });

  // Assign each event to its time group
  sortedEvents.forEach(event => {
    const eventDate = getTimestamp(event).toDate();
    const group = determineTimeGroup(eventDate);
    groups[group].push(event);
  });

  // Build time groupings array
  const timeGroupOrder: TimeGroup[] = [
    'today',
    'yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'earlier',
  ];

  const result: TimeGrouping[] = [];

  timeGroupOrder.forEach(group => {
    const groupEvents = groups[group];
    
    // Filter out empty groups
    if (groupEvents.length === 0) {
      return;
    }

    result.push({
      group,
      label: getGroupLabel(group, groupEvents.length),
      startDate: getGroupStartDate(group),
      endDate: getGroupEndDate(group),
      eventCount: groupEvents.length,
      events: groupEvents,
    });
  });

  return result;
}

/**
 * Cache for memoized groupEventsByTime results
 * Uses event IDs as cache key for efficient comparison
 */
const groupingCache = new Map<string, TimeGrouping[]>();
const MAX_CACHE_SIZE = 10; // Keep last 10 groupings

/**
 * Generate a cache key from an array of events
 * Uses event IDs, count, and avatar status to create a stable key
 */
function generateCacheKey(events: (ActivityEvent)[]): string {
  if (events.length === 0) return 'empty';
  
  // Create a key from event count, first/last event IDs, and avatar loading state
  // Include avatar URLs to invalidate cache when avatars load
  const firstId = events[0]?.id || '';
  const lastId = events[events.length - 1]?.id || '';
  const firstAvatar = events[0]?.actor?.avatar_url ? '1' : '0';
  const lastAvatar = events[events.length - 1]?.actor?.avatar_url ? '1' : '0';
  
  return `${events.length}-${firstId}-${lastId}-${firstAvatar}${lastAvatar}`;
}

/**
 * Memoized version of groupEventsByTime
 *
 * Caches results based on event array identity to avoid
 * expensive recalculations when events haven't changed.
 *
 * @param events - Array of activity events to group
 * @returns Cached or newly computed array of time groupings
 *
 * @example
 * ```tsx
 * // In a React component
 * const groupedEvents = useMemo(() =>
 *   memoizedGroupEventsByTime(events),
 *   [events]
 * );
 * ```
 */
export function memoizedGroupEventsByTime(events: (ActivityEvent)[]): TimeGrouping[] {
  const cacheKey = generateCacheKey(events);
  
  // Check cache
  const cached = groupingCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  // Compute new grouping
  const result = groupEventsByTime(events);
  
  // Add to cache
  groupingCache.set(cacheKey, result);
  
  // Maintain cache size limit (LRU-style)
  if (groupingCache.size > MAX_CACHE_SIZE) {
    const firstKey = groupingCache.keys().next().value;
    if (firstKey !== undefined) {
      groupingCache.delete(firstKey);
    }
  }
  
  return result;
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "just now")
 * 
 * @param date - The date to format
 * @returns Formatted relative time string
 * 
 * @example
 * ```tsx
 * formatDistanceToNow(new Date(Date.now() - 5 * 60 * 1000)); // "5 minutes ago"
 * formatDistanceToNow(new Date(Date.now() - 2 * 60 * 60 * 1000)); // "2 hours ago"
 * ```
 */
export function formatDistanceToNow(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}